/**
 * Settings storage — v0M15.4.
 *
 * Persistent user preferences, stored as a single JSON blob under
 * `su:settings` in localStorage. Consumed by:
 *   - `src/client/audio.ts` (SFX / music volume + muted)
 *   - v0M15.5 in-game autosave loop (autosave frequency)
 *   - v0M15.4b (planned) keybindings surface
 *
 * Design lock (v0M15-design):
 *   - Live-update model: sliders in the Settings modal apply values as
 *     the user drags. No separate "commit" step. Rationale: matches
 *     modern-game audio-slider UX; means the user can hear/see the
 *     effect in real time.
 *   - Single storage key: settings are one small object (~200 bytes),
 *     so a single JSON blob is simpler than the per-key convention
 *     used by save-storage. If settings grow to hundreds of keys or
 *     need per-key partial-update semantics, split later.
 *   - `subscribe()` fan-out: consumers (audio module, autosave loop)
 *     register a listener at boot and re-apply whenever settings change.
 *     Removes the need for a global event-bus.
 */

/**
 * Frequency options for the in-game autosave loop. Consumed by v0M15.5.
 * String union (not raw milliseconds) so the UI can round-trip the
 * dropdown selection cleanly and future values (e.g. 'per-turn') stay
 * self-descriptive.
 */
export type AutosaveFrequency = 'off' | '5min' | '15min' | '30min';

export const AUTOSAVE_FREQUENCY_LABELS: Readonly<Record<AutosaveFrequency, string>> = {
  off: 'Off',
  '5min': 'Every 5 minutes',
  '15min': 'Every 15 minutes',
  '30min': 'Every 30 minutes',
};

/** Convert an AutosaveFrequency to a millisecond interval (or null for 'off'). */
export function autosaveFrequencyMs(f: AutosaveFrequency): number | null {
  switch (f) {
    case 'off': return null;
    case '5min': return 5 * 60 * 1000;
    case '15min': return 15 * 60 * 1000;
    case '30min': return 30 * 60 * 1000;
  }
}

export interface AudioSettings {
  /** SFX bus volume, 0.0 (silent) to 1.0 (max). */
  sfxVolume: number;
  /** Music bus volume, 0.0 to 1.0. Not yet wired — no music system at v0M15.4. */
  musicVolume: number;
  /** Global mute — overrides both volume sliders when true. */
  muted: boolean;
}

export interface AutosaveSettings {
  frequency: AutosaveFrequency;
}

/**
 * v0M15.4b (2026-07-30) — keybinding entry.
 *
 * `code` is a `KeyboardEvent.code` string (e.g. `'Space'`, `'Escape'`,
 * `'KeyA'`, `'ShiftLeft'`). Chosen over `KeyboardEvent.key` because
 * `.code` is layout-independent — a QWERTY 'Space' binding still fires
 * on Dvorak, Colemak, and non-Latin keyboards.
 *
 * Special-case sentinel: `code = 'Shift'` matches either `ShiftLeft` OR
 * `ShiftRight` at dispatch time (a shift-key-agnostic default). Any
 * concrete `Shift*` code binds to that specific side.
 *
 * `label` is the display string surfaced in the Settings modal ("Space",
 * "Esc", "Left Shift", etc.). Kept alongside `code` so the display
 * doesn't need to re-derive on every render.
 */
export interface Keybinding {
  code: string;
  label: string;
}

/**
 * Rebindable actions at v1. Deliberately narrow — only three:
 *  - `pause`: toggles the sim-driver pause state (default Space).
 *  - `openMenu`: opens the in-game menu (default Escape). Also serves as
 *    a "clear pending selection / picker" escape hatch via the input
 *    priority chain in `main.ts`.
 *  - `fastPan`: modifier that multiplies keyboard + edge-scroll speeds
 *    while held (default Shift — either side).
 *
 * Not rebindable at v1 (kept hardcoded): `Enter` (commit polyline),
 * `Backtick` (dev console), `Tab` (battle-view toggle), `KeyA` (assault
 * shortcut), arrow / WASD pan keys. If Michael flags a rebindable set
 * at playtest, extend the union + `DEFAULT_KEYBINDINGS` + the reader in
 * `input.ts` / `main.ts` accordingly.
 */
export type KeybindableAction = 'pause' | 'openMenu' | 'fastPan';

export type KeybindingMap = Record<KeybindableAction, Keybinding>;

/**
 * Human-readable labels for the rebindable actions themselves — surfaced
 * as the row label in the Settings modal.
 */
export const KEYBINDABLE_ACTION_LABELS: Readonly<Record<KeybindableAction, string>> = {
  pause: 'Pause / Resume',
  openMenu: 'Open Menu',
  fastPan: 'Fast Pan (hold)',
};

export const DEFAULT_KEYBINDINGS: KeybindingMap = {
  pause: { code: 'Space', label: 'Space' },
  openMenu: { code: 'Escape', label: 'Esc' },
  fastPan: { code: 'Shift', label: 'Shift' },
};

export interface Settings {
  readonly audio: AudioSettings;
  readonly autosave: AutosaveSettings;
  /**
   * v0M15.4b (2026-07-30) — rebindable keys. Every read from storage
   * shape-fills missing entries from `DEFAULT_KEYBINDINGS` so a pre-v0M15.4b
   * saved settings blob keeps working (forward-compat: absent = default).
   */
  readonly keybindings: KeybindingMap;
}

export const DEFAULT_SETTINGS: Settings = {
  audio: {
    sfxVolume: 0.7,
    musicVolume: 0.5,
    muted: false,
  },
  autosave: {
    frequency: '15min',
  },
  keybindings: DEFAULT_KEYBINDINGS,
};

const STORAGE_KEY = 'su:settings';

// ---------------------------------------------------------------------------
// Live state + subscribers
// ---------------------------------------------------------------------------

/**
 * In-memory copy of the current settings. Loaded from localStorage on
 * first read; kept in sync with subsequent writes. All consumers should
 * read via `loadSettings()` rather than touching this directly.
 */
let cached: Settings | null = null;
const subscribers = new Set<(s: Settings) => void>();

/**
 * Load settings from localStorage (or return the in-memory cache if
 * already loaded). Missing keys / malformed JSON fall back to defaults.
 * Idempotent — safe to call anywhere.
 */
export function loadSettings(): Settings {
  if (cached !== null) return cached;
  cached = readFromStorage() ?? { ...DEFAULT_SETTINGS };
  return cached;
}

/**
 * Merge a partial update into the current settings, persist to
 * localStorage, and notify subscribers. Partial-update shape keeps
 * callers from having to round-trip every field.
 */
export function updateSettings(patch: {
  audio?: Partial<AudioSettings>;
  autosave?: Partial<AutosaveSettings>;
  /**
   * v0M15.4b — partial keybinding update. Pass one or more action keys
   * with their new `Keybinding` value; unspecified actions retain
   * current bindings. To reset a single action to default, pass its
   * `DEFAULT_KEYBINDINGS[action]` entry.
   */
  keybindings?: Partial<KeybindingMap>;
}): Settings {
  const current = loadSettings();
  const next: Settings = {
    audio: { ...current.audio, ...(patch.audio ?? {}) },
    autosave: { ...current.autosave, ...(patch.autosave ?? {}) },
    keybindings: { ...current.keybindings, ...(patch.keybindings ?? {}) },
  };
  // Clamp volume values to [0, 1] defensively — malformed input from a
  // dev-console tweak or a future migration shouldn't propagate.
  next.audio.sfxVolume = clamp01(next.audio.sfxVolume);
  next.audio.musicVolume = clamp01(next.audio.musicVolume);
  cached = next;
  writeToStorage(next);
  for (const cb of subscribers) cb(next);
  return next;
}

/**
 * Register a callback to run whenever settings change. Returns an
 * unsubscribe function. The callback is NOT invoked with the current
 * value — call `loadSettings()` at the same time if you need seeding.
 */
export function subscribeSettings(cb: (s: Settings) => void): () => void {
  subscribers.add(cb);
  return () => subscribers.delete(cb);
}

/** Reset in-memory cache (test helper). */
export function _resetSettingsCache(): void {
  cached = null;
}

// ---------------------------------------------------------------------------
// Persistence helpers
// ---------------------------------------------------------------------------

function readFromStorage(): Settings | null {
  if (typeof localStorage === 'undefined') return null;
  const raw = localStorage.getItem(STORAGE_KEY);
  if (raw === null) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<Settings> & {
      keybindings?: Partial<Record<KeybindableAction, Partial<Keybinding>>>;
    };
    // Shape-check + fill missing fields from defaults. Allows the
    // stored settings to survive a defaults-schema evolution: new
    // fields inherit the default value silently.
    return {
      audio: {
        sfxVolume: numOr(parsed.audio?.sfxVolume, DEFAULT_SETTINGS.audio.sfxVolume),
        musicVolume: numOr(parsed.audio?.musicVolume, DEFAULT_SETTINGS.audio.musicVolume),
        muted: typeof parsed.audio?.muted === 'boolean' ? parsed.audio.muted : DEFAULT_SETTINGS.audio.muted,
      },
      autosave: {
        frequency: validFreq(parsed.autosave?.frequency) ?? DEFAULT_SETTINGS.autosave.frequency,
      },
      // v0M15.4b — per-action shape-fill from defaults so a pre-v0M15.4b
      // saved settings blob (which has no `keybindings` key at all)
      // works: every action key falls back individually.
      keybindings: {
        pause: validKeybinding(parsed.keybindings?.pause) ?? DEFAULT_KEYBINDINGS.pause,
        openMenu: validKeybinding(parsed.keybindings?.openMenu) ?? DEFAULT_KEYBINDINGS.openMenu,
        fastPan: validKeybinding(parsed.keybindings?.fastPan) ?? DEFAULT_KEYBINDINGS.fastPan,
      },
    };
  } catch {
    return null;
  }
}

/**
 * v0M15.4b — validate a persisted keybinding entry. Returns the entry
 * verbatim when both `code` and `label` are non-empty strings; null
 * otherwise (caller substitutes the default).
 */
function validKeybinding(entry: Partial<Keybinding> | undefined): Keybinding | null {
  if (!entry) return null;
  if (typeof entry.code !== 'string' || entry.code.length === 0) return null;
  if (typeof entry.label !== 'string' || entry.label.length === 0) return null;
  return { code: entry.code, label: entry.label };
}

function writeToStorage(s: Settings): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
  } catch {
    // Quota shouldn't be reachable for a ~200-byte payload; if it is,
    // the settings are still live in-memory for this session.
  }
}

function numOr(v: unknown, fallback: number): number {
  return typeof v === 'number' && Number.isFinite(v) ? clamp01(v) : fallback;
}

function validFreq(v: unknown): AutosaveFrequency | null {
  return v === 'off' || v === '5min' || v === '15min' || v === '30min' ? v : null;
}

function clamp01(n: number): number {
  if (n < 0) return 0;
  if (n > 1) return 1;
  return n;
}
